// Panel Ruta: capta origen/destino (por ubicación, por click en el mapa o dirección con
// Google), llama al planificador, pinta el resultado en el sheet y dibuja la ruta en el mapa.

import { pathBetweenStops, type Dataset, type LineId } from "../data";
import type { MapAdapter, RouteLegDraw } from "../map/types";
import type { LatLng } from "../geo";
import { route, type Leg, type RouteResult } from "../router";
import { fmtMinutes, stopLabel } from "../format";
import { clipBetween } from "../geo";

export interface RoutePanelOptions {
  ds: Dataset;
  adapter: MapAdapter;
  oTxt: HTMLInputElement;
  dTxt: HTMLInputElement;
  oGps: HTMLButtonElement;
  oPick: HTMLButtonElement;
  dPick: HTMLButtonElement;
  res: HTMLDivElement;
  toast: (msg: string) => void;
  requestGps: (cb: (ll: LatLng) => void) => void;
  onEnterAddress?: (which: "o" | "d", query: string) => Promise<{ ll: LatLng; label: string } | null>;
  // Callback para que el shell pueda plegar el sheet cuando pedimos elegir en el mapa.
  onArmPick: (armed: boolean) => void;
  // Cuando cambia la selección de líneas por una ruta, para que el LinesPanel pueda guardar y
  // restaurar el estado anterior al volver a la pestaña Líneas.
  onLinesUsed: (used: LineId[]) => void;
}

export interface RoutePanelHandle {
  setOrigin: (ll: LatLng | null, label?: string) => void;
  setDestination: (ll: LatLng | null, label?: string) => void;
  setEndpointFromStop: (stopIdx: number, role: "origin" | "destination") => void;
  arm: (which: "o" | "d" | null) => void;
  isArmed: () => "o" | "d" | null;
  plan: () => void;
  reset: () => void;
}

export function mountRoutePanel(o: RoutePanelOptions): RoutePanelHandle {
  const state: { o: LatLng | null; d: LatLng | null } = { o: null, d: null };
  let armed: "o" | "d" | null = null;

  const setPoint = (which: "o" | "d", ll: LatLng | null, label?: string): void => {
    state[which] = ll;
    const input = which === "o" ? o.oTxt : o.dTxt;
    input.value = ll ? (label ?? `${ll.lat.toFixed(5)}, ${ll.lon.toFixed(5)}`) : "";
    o.adapter.setPin(which === "o" ? "origin" : "destination", ll);
    plan();
  };

  const arm = (which: "o" | "d" | null): void => {
    armed = which;
    o.oPick.classList.toggle("arm", armed === "o");
    o.dPick.classList.toggle("arm", armed === "d");
    o.onArmPick(armed !== null);
    if (armed) o.toast(`Toca el mapa para fijar el ${armed === "o" ? "origen" : "destino"}.`);
  };

  const legToDraw = (leg: Leg): RouteLegDraw | null => {
    if (leg.type === "walk") {
      if (!leg.from || !leg.to) return null;
      return { type: "walk", from: leg.from, to: leg.to };
    }
    if (leg.type === "bus") {
      const line = leg.line ?? "";
      const busLine = o.ds.linesById.get(line);
      if (!busLine || !leg.stops || leg.stops.length < 2) return null;
      const boardIdx = leg.stops[0]!;
      const alightIdx = leg.stops[leg.stops.length - 1]!;
      const boardStop = o.ds.stopsByIdx[boardIdx];
      const alightStop = o.ds.stopsByIdx[alightIdx];
      if (!boardStop || !alightStop) return null;
      // Trazado oficial por calles entre subida y bajada en el sentido del bus. Si ningún
      // trayecto las recorre en ese orden, recorte del trazado de la línea y, en último caso,
      // unión recta de las paradas del tramo.
      const path =
        pathBetweenStops(o.ds, line, boardIdx, alightIdx) ??
        clipBetween(busLine.paths, boardStop.ll, alightStop.ll) ??
        leg.stops.map((i) => o.ds.stopsByIdx[i]!.ll);
      return { type: "bus", line, color: busLine.color, path, boardIdx, alightIdx };
    }
    return null;
  };

  const showRoute = (r: RouteResult | null, isAlt: boolean, banned?: LineId): void => {
    if (!r) {
      o.res.innerHTML = `<p class="note">No he encontrado ruta.</p>`;
      o.adapter.drawRoute(null);
      return;
    }
    const legsHtml = r.legs
      .map((g) => {
        if (g.type === "walk") {
          return `<div class="leg"><i>🚶</i><div>Camina ${g.meters ?? 0} m</div><u>${fmtMinutes(
            g.time,
          )}</u></div>`;
        }
        if (g.type === "wait") {
          return `<div class="leg"><i style="--c:#3a4c5c">⏱</i><div>Espera la ${g.line}<small>media estimada, sin horarios</small></div><u>${fmtMinutes(
            g.time,
          )}</u></div>`;
        }
        const line = o.ds.linesById.get(g.line ?? "");
        if (!line || !g.stops) return "";
        const a = o.ds.stopsByIdx[g.stops[0]!]!;
        const z = o.ds.stopsByIdx[g.stops[g.stops.length - 1]!]!;
        return `<div class="leg" style="--c:${line.color}"><i>${line.id}</i><div>${escapeHtml(
          stopLabel(a),
        )} → ${escapeHtml(stopLabel(z))}<small>${g.stops.length - 1} paradas · ${escapeHtml(
          line.name,
        )}</small></div><u>${fmtMinutes(g.time)}</u></div>`;
      })
      .join("");
    const totalLine = r.lines.length
      ? `en ${r.lines.join(" + ")} · a pie serían ${fmtMinutes(r.walkOnlySeconds)}`
      : "andando, no compensa coger el bus";
    const altBtn =
      r.lines.length > 0 && !isAlt
        ? `<button class="alt" id="altBtn" type="button">Ver alternativa sin la ${r.lines[0]}</button>`
        : "";
    const bestBtn = isAlt
      ? `<button class="alt" id="bestBtn" type="button">Volver a la más rápida</button>`
      : "";
    o.res.innerHTML = `
      <div class="tot"><b>${fmtMinutes(r.totalSeconds)}</b><span>${totalLine}</span></div>
      ${legsHtml}
      ${altBtn}${bestBtn}
      <p class="note">Tiempos orientativos: bus a 15 km/h de media y 6 min de espera por trasbordo. No se usan horarios reales y el sentido de circulación no se tiene en cuenta, por lo que en circulares y tramos de sentido único la ruta puede no ser válida.</p>
    `;
    o.res.querySelector<HTMLButtonElement>("#altBtn")?.addEventListener("click", () => {
      if (!state.o || !state.d) return;
      const first = r.lines[0];
      if (!first) return;
      const alt = route(o.ds, state.o, state.d, { banLines: new Set([first]) });
      showRoute(alt, true, first);
    });
    o.res.querySelector<HTMLButtonElement>("#bestBtn")?.addEventListener("click", () => {
      plan();
    });

    // Dibujado y notificación de líneas usadas.
    const draws: RouteLegDraw[] = [];
    for (const leg of r.legs) {
      const d = legToDraw(leg);
      if (d) draws.push(d);
    }
    o.adapter.drawRoute({ legs: draws });
    o.onLinesUsed(r.lines);
    void banned;
  };

  const plan = (): void => {
    if (!state.o || !state.d) {
      o.res.innerHTML = `<p class="note">Indica origen y destino. Puedes usar tu ubicación (◎), tocar el mapa (⌖) o, con la clave de Google, escribir una dirección y pulsar Intro.</p>`;
      return;
    }
    const r = route(o.ds, state.o, state.d);
    showRoute(r, false);
  };

  o.oPick.addEventListener("click", () => arm(armed === "o" ? null : "o"));
  o.dPick.addEventListener("click", () => arm(armed === "d" ? null : "d"));
  o.oGps.addEventListener("click", () =>
    o.requestGps((ll) => {
      setPoint("o", ll, "Mi ubicación");
    }),
  );

  for (const which of ["o", "d"] as const) {
    const input = which === "o" ? o.oTxt : o.dTxt;
    input.addEventListener("keydown", (ev) => {
      if (ev.key !== "Enter") return;
      const q = input.value.trim();
      if (!q) return;
      if (!o.onEnterAddress) {
        o.toast("Para buscar direcciones hace falta la clave de Google (⚙). Mientras tanto, usa ⌖ y toca el mapa.");
        return;
      }
      void o.onEnterAddress(which, q).then((r) => {
        if (!r) {
          o.toast("No encuentro esa dirección.");
          return;
        }
        setPoint(which, r.ll, r.label);
      });
    });
  }

  // Enganchamos el click del mapa aquí también, filtrado por armed. El main decide si estamos
  // en pestaña Ruta antes de propagarlo (para no capturar clicks fuera de contexto).
  o.adapter.onClick((ll) => {
    if (armed) {
      const which = armed;
      arm(null);
      setPoint(which, ll);
    }
  });

  return {
    setOrigin: (ll, label) => setPoint("o", ll, label),
    setDestination: (ll, label) => setPoint("d", ll, label),
    setEndpointFromStop: (stopIdx, role) => {
      const stop = o.ds.stopsByIdx[stopIdx];
      if (!stop) return;
      setPoint(role === "origin" ? "o" : "d", stop.ll, stopLabel(stop));
    },
    arm: (w) => arm(w),
    isArmed: () => armed,
    plan,
    reset: () => {
      setPoint("o", null);
      setPoint("d", null);
      o.res.innerHTML = "";
      o.adapter.drawRoute(null);
    },
  };
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
